import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Hidden,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core';
import moment from 'moment';
import {
  CheckCircleRounded as ApproveIcon,
  CancelRounded as RejectIcon,
  MoneyRounded as ExpenseIcon,
  BeachAccessRounded as LeaveIcon
} from '@material-ui/icons';

import styled from 'styled-components';

import noActionsImg from 'images/zen.jpg';
import { ExpenseClaim, LeaveRequest } from 'domain-types';
import { theme } from 'theme';

import {
  ConfirmDialog,
  CustomDialog,
  getLeaveIcon,
  getLeaveTypeIcon,
  Tabs,
  Table,
  LimitedText,
  CollapsableTableRow,
  FormRow,
  FormLabel,
  FormTextValue
} from 'components';
import { Alert } from '@material-ui/lab';
import {
  AuthContext,
  isAnyOrgFeatureEnabled,
  isOrgFeatureEnabled,
  isOrgReadOnly,
  localizeLeaveType,
  useSpot
} from 'framework';
import { getExpenseClaimDisplayDate } from './expense-claim-date';

const ApproveIconStyled = styled(ApproveIcon)`
  color: ${theme.palette.success.main};
`;
const RejectIconStyled = styled(RejectIcon)`
  color: ${theme.palette.error.main};
`;

const NoActionsBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CenterButtons = styled(Box)`
  display: flex;
  justify-content: center;
`;

interface ConfirmDialogDesc {
  title: string;
  text: string;
  callback: () => Promise<void>;
}

export function Actions() {
  const { t } = useTranslation();
  const history = useHistory();
  const { organization } = useParams<{ organization: string }>();
  const { getCurrentUser } = useContext(AuthContext);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogDesc | null>(
    null
  );
  const [rejectExpenseDialog, setRejectExpenseDialog] = useState<{
    claim: ExpenseClaim;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'leave' | 'expenses'>('leave');

  const id = getCurrentUser();
  const { data, errors, query, command } = useSpot();
  const org = data?.organizations?.[organization];
  const hasLoadedOrg = !!org;
  const readOnly = isOrgReadOnly(org);
  const leaveEnabled = isOrgFeatureEnabled(org, 'leave');
  const expensesEnabled = isOrgFeatureEnabled(org, 'expenses');
  const hasActionFeatures = isAnyOrgFeatureEnabled(org, ['leave', 'expenses']);

  useEffect(() => {
    if (errors.find(err => err.status === 401)) {
      window.location.reload();
    }
  }, [errors]);

  useEffect(() => {
    if (hasLoadedOrg) {
      return;
    }

    (async () => {
      setLoading(true);
      await query(`org/${organization}`, {}, ['organizations', organization]);
      setLoading(false);
    })();
  }, [hasLoadedOrg, organization, query]);

  useEffect(() => {
    if (!hasLoadedOrg || !hasActionFeatures) {
      return;
    }

    (async () => {
      setLoading(true);
      const requests: Promise<unknown>[] = [];

      if (leaveEnabled) {
        requests.push(
          query(`org/${organization}/user/${id}/leaveApproval`, {}, [
            'actions',
            'leave'
          ])
        );
      }

      if (expensesEnabled) {
        requests.push(
          query(`org/${organization}/user/${id}/expenseApproval`, {}, [
            'actions',
            'expenses'
          ])
        );
      }

      await Promise.all(requests);
      setLoading(false);
    })();
  }, [
    expensesEnabled,
    hasActionFeatures,
    hasLoadedOrg,
    id,
    leaveEnabled,
    organization,
    query
  ]);

  useEffect(() => {
    if (leaveEnabled && !expensesEnabled) {
      setActiveTab('leave');
      return;
    }

    if (!leaveEnabled && expensesEnabled) {
      setActiveTab('expenses');
    }
  }, [expensesEnabled, leaveEnabled]);

  // --- Leave handlers ---
  const onApprove = (leaveRequest: LeaveRequest) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      title: t('approveLeaveTitle'),
      text: t('approveLeaveText'),
      callback: async () => {
        await command(
          `org/${organization}/user/${leaveRequest.userId}/leave/${leaveRequest.id}/approve`
        );
        await query(`org/${organization}/user/${id}/leaveApproval`, {}, [
          'actions',
          'leave'
        ]);
        setConfirmDialog(null);
      }
    });
  };

  const onReject = (leaveRequest: LeaveRequest) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      title: t('rejectLeaveTitle'),
      text: t('rejectLeaveText'),
      callback: async () => {
        await command(
          `org/${organization}/user/${leaveRequest.userId}/leave/${leaveRequest.id}/reject`
        );
        await query(`org/${organization}/user/${id}/leaveApproval`, {}, [
          'actions',
          'leave'
        ]);
        setConfirmDialog(null);
      }
    });
  };

  // --- Expense handlers ---
  const onApproveExpense = (claim: ExpenseClaim) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      title: t('approveExpenseTitle'),
      text: t('approveExpenseText'),
      callback: async () => {
        await command(
          `org/${organization}/user/${
            claim.createdByUserId
          }/expenses/${encodeURIComponent(claim.id)}/approve`
        );
        await query(`org/${organization}/user/${id}/expenseApproval`, {}, [
          'actions',
          'expenses'
        ]);
        setConfirmDialog(null);
      }
    });
  };

  const onRejectExpense = (claim: ExpenseClaim) => {
    if (readOnly) {
      return;
    }

    setRejectReason('');
    setRejectExpenseDialog({ claim });
  };

  const submitRejectExpense = async () => {
    if (!rejectExpenseDialog || readOnly) return;
    const { claim } = rejectExpenseDialog;
    await command(
      `org/${organization}/user/${
        claim.createdByUserId
      }/expenses/${encodeURIComponent(claim.id)}/reject`,
      { reason: rejectReason }
    );
    await query(`org/${organization}/user/${id}/expenseApproval`, {}, [
      'actions',
      'expenses'
    ]);
    setRejectExpenseDialog(null);
  };

  const expenseClaims: ExpenseClaim[] = (data?.actions?.expenses ?? [])
    .filter(c => c.status === 'pending_approval' || c.status === 'send_failed')
    .sort((claimA, claimB) =>
      getExpenseClaimDisplayDate(claimB).localeCompare(
        getExpenseClaimDisplayDate(claimA)
      )
    );
  const leavePending =
    data?.actions?.leave?.filter(l => l.state === 'requested')?.length ?? 0;
  const expensePending = expenseClaims.length;
  const leaveTabContent = (
    <>
      {readOnly ? (
        <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
      ) : null}
      <Hidden mdUp>
        <Table
          maxHeight="600px"
          headers={[t('status'), t('type'), t('name'), '']}
          renderEmptyContent={() => (
            <NoActionsBox>
              {loading ? (
                t('loading')
              ) : (
                <>
                  <img src={noActionsImg} height={400} alt={t('noActions')} />
                  <Typography variant="h5" color="primary">
                    {t('noActions')}
                  </Typography>
                </>
              )}
            </NoActionsBox>
          )}
          renderCustomRow={(leaveRequest: LeaveRequest) => (
            <CollapsableTableRow
              key={leaveRequest.id}
              id={leaveRequest.id}
              disabled={leaveRequest.state !== 'requested'}
              row={[
                <Box key="state" title={t(leaveRequest.state)}>
                  {getLeaveIcon(leaveRequest.state)}
                </Box>,
                <Box key="type" title={localizeLeaveType(t, leaveRequest.type)}>
                  {getLeaveTypeIcon(leaveRequest.type)}
                </Box>,
                `${leaveRequest.firstName} ${leaveRequest.lastName ?? ''}`,
                ''
              ]}
              collapsedContent={
                <Box>
                  <FormRow>
                    <FormLabel>{t('reason')}</FormLabel>
                    <FormTextValue>{leaveRequest.reason}</FormTextValue>
                  </FormRow>
                  <FormRow>
                    <FormLabel>{t('when')}</FormLabel>
                    <FormTextValue>
                      {`${moment(leaveRequest.startDate).format(
                        'LL'
                      )} - ${moment(leaveRequest.endDate).format('LL')}`}
                    </FormTextValue>
                  </FormRow>
                  <CenterButtons>
                    <IconButton
                      onClick={() => onApprove(leaveRequest)}
                      disabled={readOnly}
                    >
                      <ApproveIconStyled />
                    </IconButton>
                    <IconButton
                      onClick={() => onReject(leaveRequest)}
                      disabled={readOnly}
                    >
                      <RejectIconStyled />
                    </IconButton>
                  </CenterButtons>
                </Box>
              }
            />
          )}
          rows={
            data?.actions?.leave
              ? data?.actions?.leave?.sort((reqA, reqB) => {
                  if (reqB.state !== 'requested') {
                    return -1;
                  }
                  if (reqA.state !== 'requested') {
                    return 1;
                  }
                  return reqB.startDate.localeCompare(reqA.startDate);
                })
              : []
          }
        />
      </Hidden>
      <Hidden smDown>
        <Table
          maxHeight="600px"
          headers={[
            t('status'),
            t('type'),
            t('name'),
            t('info'),
            t('start'),
            t('end'),
            ''
          ]}
          renderEmptyContent={() => (
            <NoActionsBox>
              {loading ? (
                t('loading')
              ) : (
                <>
                  <img src={noActionsImg} height={400} alt={t('noActions')} />
                  <Typography variant="h5" color="primary">
                    {t('noActions')}
                  </Typography>
                </>
              )}
            </NoActionsBox>
          )}
          rows={
            data?.actions?.leave
              ? data?.actions?.leave
                  ?.sort((reqA, reqB) => {
                    if (reqB.state !== 'requested') {
                      return -1;
                    }
                    if (reqA.state !== 'requested') {
                      return 1;
                    }
                    return reqB.startDate.localeCompare(reqA.startDate);
                  })
                  .map(leaveRequest => ({
                    id: leaveRequest.id,
                    disabled: leaveRequest.state !== 'requested',
                    cells: [
                      <Box key="state" title={t(leaveRequest.state)}>
                        {getLeaveIcon(leaveRequest.state)}
                      </Box>,
                      <Box
                        key="type"
                        title={localizeLeaveType(t, leaveRequest.type)}
                      >
                        {getLeaveTypeIcon(leaveRequest.type)}
                      </Box>,
                      `${leaveRequest.firstName} ${leaveRequest.lastName ??
                        ''}`,
                      <LimitedText
                        key="reason"
                        title={leaveRequest.reason}
                        variant="body2"
                      >
                        {leaveRequest.reason}
                      </LimitedText>,
                      moment(leaveRequest.startDate).format('LL'),
                      moment(leaveRequest.endDate).format('LL'),
                      <>
                        <IconButton
                          onClick={() => onApprove(leaveRequest)}
                          disabled={readOnly}
                        >
                          <ApproveIconStyled />
                        </IconButton>
                        <IconButton
                          onClick={() => onReject(leaveRequest)}
                          disabled={readOnly}
                        >
                          <RejectIconStyled />
                        </IconButton>
                      </>
                    ]
                  }))
              : []
          }
        />
      </Hidden>
    </>
  );
  const expensesTabContent = (
    <>
      {readOnly ? (
        <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
      ) : null}
      <Table
        maxHeight="600px"
        headers={[t('name'), t('date'), t('total'), t('status'), '']}
        renderEmptyContent={() => (
          <NoActionsBox>
            {loading ? (
              t('loading')
            ) : (
              <>
                <img src={noActionsImg} height={400} alt={t('noActions')} />
                <Typography variant="h5" color="primary">
                  {t('noExpenseClaims')}
                </Typography>
              </>
            )}
          </NoActionsBox>
        )}
        rows={expenseClaims.map(claim => ({
          id: claim.id,
          disabled: false,
          cells: [
            `${claim.firstName ?? ''} ${claim.lastName ?? ''}`.trim(),
            moment(getExpenseClaimDisplayDate(claim)).format('LL'),
            `\u20ac${claim.totalAmountIncl.toFixed(2)}`,
            claim.status === 'send_failed' ? (
              <Typography key="err" variant="body2" color="error">
                {t('claimSendFailed')}
              </Typography>
            ) : (
              t('claimPendingApproval')
            ),
            <>
              <IconButton
                key="approve"
                title={t('approveExpenseTitle')}
                onClick={event => {
                  event.stopPropagation();
                  onApproveExpense(claim);
                }}
                disabled={readOnly}
              >
                <ApproveIconStyled />
              </IconButton>
              <IconButton
                key="reject"
                title={t('rejectExpenseTitle')}
                onClick={event => {
                  event.stopPropagation();
                  onRejectExpense(claim);
                }}
                disabled={readOnly}
              >
                <RejectIconStyled />
              </IconButton>
            </>
          ]
        }))}
        onRowClick={row =>
          history.push(
            `/${organization}/expenses/${encodeURIComponent(
              row.id
            )}?from=actions`
          )
        }
      />
    </>
  );
  const actionTabs: { [k: string]: JSX.Element } = {
    ...(leaveEnabled ? { leave: leaveTabContent } : {}),
    ...(expensesEnabled ? { expenses: expensesTabContent } : {})
  };
  const actionTabOptions = {
    ...(leaveEnabled
      ? {
          leave: {
            icon: <LeaveIcon />,
            label: `${t('leaveRequests')}${
              leavePending > 0 ? ` (${leavePending})` : ''
            }`
          }
        }
      : {}),
    ...(expensesEnabled
      ? {
          expenses: {
            icon: <ExpenseIcon />,
            label: `${t('expenses')}${
              expensePending > 0 ? ` (${expensePending})` : ''
            }`
          }
        }
      : {})
  };

  if (org && Object.keys(actionTabs).length === 0) {
    return <Redirect to={`/${organization}`} />;
  }

  return (
    <>
      <Tabs
        selected={activeTab}
        tabs={actionTabs}
        tabOptions={actionTabOptions}
        onTabChange={tab => setActiveTab(tab as 'leave' | 'expenses')}
        loading={loading}
      />

      <ConfirmDialog
        title={confirmDialog?.title ?? ''}
        text={confirmDialog?.text ?? ''}
        dialogOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onSubmit={confirmDialog?.callback}
      />

      <CustomDialog
        title={t('rejectExpenseTitle')}
        dialogOpen={!!rejectExpenseDialog}
        onClose={() => setRejectExpenseDialog(null)}
        onSubmit={submitRejectExpense}
        renderControl={() => (
          <TextField
            label={t('rejectReason')}
            placeholder={t('rejectReasonPlaceholder')}
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
          />
        )}
      />
    </>
  );
}
